import { client } from '@/client';
import { EntityEnum } from '@/client/users';
import {
  CSV_UPLOAD_ERRORS_KEY,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { useNotifications } from '@/hooks/notifications.hook';
import { OPERATION_LOGS_QUERY_KEY } from '@/hooks/query/operation-log.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { parseImportNotification } from '@/utils/helpers';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

export type ImportIssue = {
  type: 'warnings' | 'errors';
  row: number | string;
  column?: string;
  messages: string[];
};

export type ImportStats = {
  successful?: number;
  total?: number;
};

export type ImportLog = {
  issues: ImportIssue[];
  stats?: ImportStats;
};

type StartTrackingOptions = {
  entity: EntityEnum;
  notificationId: string;
  isDryRun: boolean;
  skipNew: boolean;
  skipExisting: boolean;
  deleteNotInFile: boolean;
  restoreSoftDeleted: boolean;
  onDryRunStats?: (stats: {
    usersAdded: number | undefined;
    usersUpdated: number | undefined;
    usersRemoved: number | undefined;
    usersWithUnverifiedDomain: number | undefined;
    usersSoftDeleted: number | undefined;
  }) => void;
  onSettled?: (isDryRun: boolean) => void;
};

type UsersImportState = {
  importLog: ImportLog;
  issueCount: number;
  startTracking: (options: StartTrackingOptions) => void;
  waitForConnection: (entity: EntityEnum) => Promise<boolean>;
};

type ActiveImport = StartTrackingOptions;

const readImportLog = (): ImportLog => {
  const stored = localStorage.getItem(CSV_UPLOAD_ERRORS_KEY);

  if (!stored) {
    return { issues: [] };
  }

  try {
    const parsed = JSON.parse(stored);

    if (Array.isArray(parsed)) {
      return { issues: parsed };
    }

    return {
      issues: parsed?.issues || [],
      stats: parsed?.stats,
    };
  } catch {
    return { issues: [] };
  }
};

const normalizeImportIssues = (
  type: 'warnings' | 'errors',
  messageWarningsOrErrors: any,
): ImportIssue[] =>
  Object.values(messageWarningsOrErrors || {}).flatMap((row: any) =>
    Object.entries(row?.[type] || {}).map(([column, items]: [string, any]) => {
      const messages = Array.isArray(items) ? items.flat() : [String(items)];

      return {
        type,
        row: row?.rowNumber ?? '-',
        column,
        messages,
      };
    }),
  );

const UsersImportContext = createContext<UsersImportState>({
  importLog: { issues: [] },
  issueCount: 0,
  startTracking: () => undefined,
  waitForConnection: async () => false,
});

const WAIT_FOR_CONNECTION_TIMEOUT_MS = 8000;

export const useUsersImportMessage = () => useContext(UsersImportContext);

export const UsersImportMessage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [importLog, setImportLog] = useState<ImportLog>(readImportLog());

  const activeImportRef = useRef<ActiveImport | null>(null);

  const {
    lastMessage: accountLastMessage,
    setNotificationParam: setAccountNotificationParam,
    notificationParam: accountNotificationParam,
    isConnected: isAccountNotifyConnected,
  } = useNotifications(client.accounts.accountsNotifyUrl);

  const {
    lastMessage: branchLastMessage,
    setNotificationParam: setBranchNotificationParam,
    notificationParam: branchNotificationParam,
    isConnected: isBranchNotifyConnected,
  } = useNotifications(client.branches.branchesNotifyUrl);

  const {
    lastMessage: groupLastMessage,
    setNotificationParam: setGroupNotificationParam,
    notificationParam: groupNotificationParam,
    isConnected: isGroupNotifyConnected,
  } = useNotifications(client.groups.groupsNotifyUrl);

  const persistImportLogToLocalStorage = useCallback(
    (nextImportLog: ImportLog) => {
      if (nextImportLog.issues.length === 0) {
        localStorage.removeItem(CSV_UPLOAD_ERRORS_KEY);
      } else {
        localStorage.setItem(
          CSV_UPLOAD_ERRORS_KEY,
          JSON.stringify(nextImportLog),
        );
      }

      setImportLog(nextImportLog);
    },
    [],
  );

  const clearTracking = useCallback(
    (entity: EntityEnum) => {
      if (entity === EntityEnum.ACCOUNT) {
        setAccountNotificationParam(undefined);
      }
      if (entity === EntityEnum.BRANCH) {
        setBranchNotificationParam(undefined);
      }
      if (entity === EntityEnum.GROUP) {
        setGroupNotificationParam(undefined);
      }
      activeImportRef.current = null;
    },
    [
      setAccountNotificationParam,
      setBranchNotificationParam,
      setGroupNotificationParam,
    ],
  );

  const handleEntityNotification = useCallback(
    (messages: any, entity: EntityEnum) => {
      const activeImport = activeImportRef.current;

      if (!activeImport || activeImport.entity !== entity) {
        return;
      }

      if (
        !messages?.data?.event ||
        ![
          `${entity}.users.import.finished`,
          `${entity}.users.import.failed`,
        ].includes(messages?.data?.event)
      ) {
        return;
      }

      if (messages.data.event === `${entity}.users.import.finished`) {
        if (activeImport.isDryRun) {
          activeImport.onDryRunStats?.({
            usersAdded: activeImport.skipNew
              ? undefined
              : messages.data?.payload?.stats?.created || 0,
            usersUpdated: activeImport.skipExisting
              ? undefined
              : messages.data?.payload?.stats?.updated || 0,
            usersRemoved: activeImport.deleteNotInFile
              ? messages.data?.payload?.stats?.deleted || 0
              : undefined,
            usersWithUnverifiedDomain:
              messages.data?.payload?.stats?.withUnverifiedDomain ?? undefined,
            usersSoftDeleted:
              messages.data?.payload?.stats?.softDeleted ?? undefined,
          });
        } else {
          const warningIssues = normalizeImportIssues(
            'warnings',
            messages.data.payload?.warnings,
          );
          const errorIssues = normalizeImportIssues(
            'errors',
            messages.data.payload?.errors,
          );
          const issues = [...errorIssues, ...warningIssues];
          const successful = messages.data?.payload?.successful;
          const total = messages.data?.payload?.total;

          if (issues.length > 0) {
            persistImportLogToLocalStorage({
              issues,
              stats: {
                successful,
                total,
              },
            });

            toast?.action({
              severity: errorIssues.length > 0 ? 'error' : 'warn',
              summary:
                errorIssues.length > 0 ? t('toast.error') : t('toast.info'),
              detail: t('users.import.issues.count', {
                count: issues.length,
              }),
              actionLabel: t('generic.view.details'),
              onAction: () => {
                if (!currentAccount?.id) {
                  return;
                }
                navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS]());
              },
            });
          } else {
            persistImportLogToLocalStorage({ issues: [] });
            parseImportNotification(messages, entity, toast, t);
          }

          const demotedAdmins: string[] =
            messages.data?.payload?.demotedAdmins ?? [];
          if (demotedAdmins.length > 0) {
            toast?.action({
              severity: 'warn',
              summary: '',
              detail: t('users.import.demotedAdmins.detail', {
                names: demotedAdmins.join(', '),
              }),
              actionLabel: t('users.import.demotedAdmins.action'),
              onAction: () => {
                navigate(
                  RedirectPaths[
                    RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY_DOMAINS
                  ](),
                );
              },
            });
          }
        }
      }

      if (
        messages.data.event === `${entity}.users.import.failed` &&
        !activeImport.isDryRun
      ) {
        parseImportNotification(messages, entity, toast, t);
      }

      if (!activeImport.isDryRun) {
        queryClient.invalidateQueries(OPERATION_LOGS_QUERY_KEY);
      }

      activeImport.onSettled?.(activeImport.isDryRun);
      clearTracking(entity);
    },
    [
      clearTracking,
      currentAccount?.id,
      navigate,
      persistImportLogToLocalStorage,
      t,
      toast,
    ],
  );

  useEffect(() => {
    if (!accountNotificationParam) {
      return;
    }
    handleEntityNotification(accountLastMessage, EntityEnum.ACCOUNT);
  }, [
    accountNotificationParam,
    handleEntityNotification,
    JSON.stringify(accountLastMessage),
  ]);

  useEffect(() => {
    if (!branchNotificationParam) {
      return;
    }
    handleEntityNotification(branchLastMessage, EntityEnum.BRANCH);
  }, [
    branchNotificationParam,
    handleEntityNotification,
    JSON.stringify(branchLastMessage),
  ]);

  useEffect(() => {
    if (!groupNotificationParam) {
      return;
    }
    handleEntityNotification(groupLastMessage, EntityEnum.GROUP);
  }, [
    groupNotificationParam,
    handleEntityNotification,
    JSON.stringify(groupLastMessage),
  ]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CSV_UPLOAD_ERRORS_KEY) {
        return;
      }
      setImportLog(readImportLog());
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const startTracking = useCallback(
    ({
      entity,
      notificationId,
      isDryRun,
      skipNew,
      skipExisting,
      deleteNotInFile,
      restoreSoftDeleted,
      onDryRunStats,
      onSettled,
    }: StartTrackingOptions) => {
      activeImportRef.current = {
        entity,
        notificationId,
        isDryRun,
        skipNew,
        skipExisting,
        deleteNotInFile,
        restoreSoftDeleted,
        onDryRunStats,
        onSettled,
      };

      if (entity === EntityEnum.ACCOUNT) {
        setAccountNotificationParam(notificationId);
      }
      if (entity === EntityEnum.BRANCH) {
        setBranchNotificationParam(notificationId);
      }
      if (entity === EntityEnum.GROUP) {
        setGroupNotificationParam(notificationId);
      }
    },
    [
      setAccountNotificationParam,
      setBranchNotificationParam,
      setGroupNotificationParam,
    ],
  );

  const waitForConnection = useCallback(
    async (entity: EntityEnum) => {
      const isConnected =
        (entity === EntityEnum.ACCOUNT && isAccountNotifyConnected.current) ||
        (entity === EntityEnum.BRANCH && isBranchNotifyConnected.current) ||
        (entity === EntityEnum.GROUP && isGroupNotifyConnected.current);

      if (isConnected) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        let isSettled = false;

        const finish = (connected: boolean) => {
          if (isSettled) {
            return;
          }

          isSettled = true;
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          resolve(connected);
        };

        const intervalId = setInterval(() => {
          const nextIsConnected =
            (entity === EntityEnum.ACCOUNT &&
              isAccountNotifyConnected.current) ||
            (entity === EntityEnum.BRANCH && isBranchNotifyConnected.current) ||
            (entity === EntityEnum.GROUP && isGroupNotifyConnected.current);

          if (nextIsConnected) {
            finish(true);
          }
        }, 100);

        const timeoutId = setTimeout(() => {
          clearTracking(entity);
          finish(false);
        }, WAIT_FOR_CONNECTION_TIMEOUT_MS);
      });
    },
    [
      clearTracking,
      isAccountNotifyConnected,
      isBranchNotifyConnected,
      isGroupNotifyConnected,
    ],
  );

  const contextValue = useMemo(
    () => ({
      importLog,
      issueCount: importLog.issues.length,
      startTracking,
      waitForConnection,
    }),
    [importLog, startTracking, waitForConnection],
  );

  return (
    <UsersImportContext.Provider value={contextValue}>
      {children}
    </UsersImportContext.Provider>
  );
};
